import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Grid2, Card, CardContent, Box, LinearProgress, FormControl, InputLabel, Select, MenuItem, useTheme, IconButton, Tooltip } from '@mui/material';
import { Assignment, Person, Schedule, Flag, ExpandMore } from '@mui/icons-material';
import { getEventsTasks } from '../actions/tasks'
import { useLocation } from 'react-router-dom';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';

const MultiViewTaskManagement = () => {
  const theme = useTheme();
  const location = useLocation();
  
  const eventIds = useMemo(() => {
    const queryParam = new URLSearchParams(location.search).get('eventIds');
    return queryParam ? queryParam.split(',') : [];
  }, [location.search]);

  const [tasks, setTasks] = useState([]);
  const [expandedTexts, setExpandedTexts] = useState({});

  // Filter states
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');

  // Fetch tasks on load
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const fetchedTasks = await getEventsTasks(eventIds);
        setTasks(fetchedTasks);
      } catch (error) {
        console.error('Failed to fetch tasks', error);
      }
    };
    fetchTasks();
  }, [eventIds]);

  const capitalizeName = (name) => {
    if (!name) return '';
    return name
      .split(' ') 
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getUniqueFilterValues = (key) => {
    const values = tasks.map(task => {
      let value = task[key];
      
      if (!value || value.trim() === '') {
        return key === 'assignee' ? 'Unassigned' : 'Uncategorized';
      }
      return value;
    });

    const uniqueValues = Array.from(new Set(
      values.map(value => value.toLowerCase())
    )).map(value => {
      if (value === 'unassigned' || value === 'uncategorized') {
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
      return capitalizeName(value);
    }).sort();

    return uniqueValues;
  };

  // Handle filtering of tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const priorityValue = task.priority || '';
      const statusValue = task.status || '';
      const assigneeValue = task.assignee || '';

      const priorityMatch = !priorityFilter ||
        (priorityFilter === 'Uncategorized' && priorityValue.trim() === '') ||
        (priorityValue === priorityFilter);

      const statusMatch = !statusFilter ||
        (statusFilter === 'Uncategorized' && statusValue.trim() === '') ||
        (statusValue === statusFilter);

      const assigneeMatch = !assigneeFilter ||
        (assigneeFilter === 'Unassigned' && assigneeValue.trim() === '') ||
        (capitalizeName(assigneeValue) === assigneeFilter);

      return priorityMatch && statusMatch && assigneeMatch;
    });
  }, [tasks, priorityFilter, statusFilter, assigneeFilter]);

  // Progress and UI helper functions
  const calculateProgress = (status) => {
    const progressMap = {
      'To Do': 0,
      Scheduled: 25,
      'In Progress': 50,
      'Pending Review': 75,
      Done: 100,
      Cancelled: 100,
      'On Hold': 100,
      Blocked: 100,
    };
    return progressMap[status] || 0;
  };

  const getProgressBarColor = (status) => {
    const colorMap = {
      Done: theme.palette.success.main,
      Cancelled: theme.palette.error.main,
      'On Hold': theme.palette.warning.main,
      Blocked: theme.palette.warning.main,
      default: theme.palette.primary.main,
    };
    return colorMap[status] || colorMap.default;
  };

  const getCardBorderColor = (status) => {
    const borderColorMap = {
      Done: '2px solid ' + theme.palette.success.main,
      Cancelled: '2px solid ' + theme.palette.error.main,
      'On Hold': '2px solid ' + theme.palette.warning.main,
      Blocked: '2px solid ' + theme.palette.warning.main,
    };
    return borderColorMap[status] || (theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.2)');
  };

  const getPriorityColor = (priority) => {
    const colorMap = {
      High: theme.palette.error.main,
      Medium: theme.palette.warning.main,
      Low: theme.palette.success.main,
    };
    return colorMap[priority] || theme.palette.text.secondary;
  };

  const handleTextExpand = (taskId, field) => {
    const key = `${taskId}-${field}`;
    setExpandedTexts(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Filter Section */}
      <Box sx={{ mb: 4, mt: { xs: 4, sm: 0 } }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 4, md: 3 }} >
            <FormControl size="medium" fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                label="Priority"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Priorities</MenuItem>
                {getUniqueFilterValues('priority').map((priority) => (
                  <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          
          <Grid2 size={{ xs: 12, sm: 4, md: 3 }} >
            <FormControl size="medium" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {getUniqueFilterValues('status').map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          
          <Grid2 size={{ xs: 12, sm: 4, md: 3 }} >
            <FormControl size="medium" fullWidth>
              <InputLabel>Assignee</InputLabel>
              <Select
                value={assigneeFilter}
                onChange={(e) => setAssigneeFilter(e.target.value)}
                label="Assignee"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Assignees</MenuItem>
                {getUniqueFilterValues('assignee').map((assignee) => (
                  <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
        </Grid2>
      </Box>

      {/* Task List */}
      <Box sx={cardGridSx()}>
        {filteredTasks.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Card sx={{ 
              p: { xs: 3, sm: 4 }, 
              textAlign: 'center',
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                  : '0 8px 30px rgba(0, 0, 0, 0.15)',
              }
            }}>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                {(priorityFilter || statusFilter || assigneeFilter) ? 
                  'No tasks found for the selected filters' : 
                  'No tasks found for the selected events'}
              </Typography>
            </Card>
          </Box>
        ) : filteredTasks.map((task) => {
          const displayAssignee = !task.assignee || task.assignee.trim() === ''
            ? 'Unassigned'
            : capitalizeName(task.assignee);

          return (
            <Box key={task.id} sx={cardItemSx()}>
              <Card sx={{
                height: '100%',
                borderRadius: 3,
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                  : '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: getCardBorderColor(task.status),
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                    : '0 8px 30px rgba(0, 0, 0, 0.15)',
                }
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Tooltip 
                    title={task.title} 
                    arrow 
                    placement="top"
                    sx={{ 
                      '& .MuiTooltip-tooltip': {
                        maxWidth: 300,
                        fontSize: '0.875rem',
                        lineHeight: 1.4
                      }
                    }}
                  >
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      mb: 2, 
                      minHeight: '2rem',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.2,
                      cursor: 'help'
                    }}>
                      {task.title}
                    </Typography>
                  </Tooltip>
                  
                  {/* Description with conditional tooltip */}
                  <Box sx={{ mb: 2, position: 'relative' }}>
                    {(() => {
                      const description = task.description || 'No description provided';
                      const isTruncated = description.length > 100;
                      const isExpanded = expandedTexts[`${task.id}-description`];
                      
                      return (
                        <>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: isExpanded ? 'unset' : 2,
                              WebkitBoxOrient: 'vertical',
                              wordBreak: 'break-word',
                              height: isExpanded ? 'auto' : '2.5rem',
                              cursor: isTruncated ? 'pointer' : 'default',
                              '&:hover': isTruncated ? {
                                backgroundColor: theme.palette.action.hover,
                                borderRadius: 1
                              } : {}
                            }}
                          >
                            {description}
                          </Typography>
                          
                          {isTruncated && (
                            <>
                              <Tooltip 
                                title={description} 
                                arrow 
                                placement="top"
                                sx={{ 
                                  display: { xs: 'none', md: 'block' },
                                  '& .MuiTooltip-tooltip': {
                                    maxWidth: 300,
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4
                                  }
                                }}
                              >
                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
                              </Tooltip>
                              
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTextExpand(task.id, 'description');
                                }}
                                sx={{
                                  position: 'absolute',
                                  bottom: -8,
                                  right: -8,
                                  backgroundColor: theme.palette.primary.main,
                                  color: theme.palette.primary.contrastText,
                                  width: 24,
                                  height: 24,
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                  },
                                  display: { xs: 'flex', md: 'none' },
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }}
                              >
                                <ExpandMore sx={{ fontSize: 16 }} />
                              </IconButton>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </Box>

                  {/* Task Details Section */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Flag sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: getPriorityColor(task.priority) }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Priority: {task.priority}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Assignment sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Status: {task.status}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Person sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Assignee: {displayAssignee}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Schedule sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Due: {task.duedate || 'No due date'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Notes Section - Fixed height */}
                  <Box sx={{ mb: 2, minHeight: '2.5rem' }}>
                    {task.notes && (() => {
                      const notes = task.notes;
                      const isTruncated = notes.length > 100;
                      const isExpanded = expandedTexts[`${task.id}-notes`];
                      
                      return (
                        <Box sx={{ position: 'relative' }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              fontSize: { xs: '0.8rem', sm: '0.875rem' }, 
                              fontStyle: 'italic',
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: isExpanded ? 'unset' : 2,
                              WebkitBoxOrient: 'vertical',
                              wordBreak: 'break-word',
                              height: isExpanded ? 'auto' : '2.5rem',
                              cursor: isTruncated ? 'pointer' : 'default',
                              '&:hover': isTruncated ? {
                                backgroundColor: theme.palette.action.hover,
                                borderRadius: 1
                              } : {}
                            }}
                          >
                            Notes: {notes}
                          </Typography>
                          
                          {isTruncated && (
                            <>
                              <Tooltip 
                                title={notes} 
                                arrow 
                                placement="top"
                                sx={{ 
                                  display: { xs: 'none', md: 'block' },
                                  '& .MuiTooltip-tooltip': {
                                    maxWidth: 300,
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4
                                  }
                                }}
                              >
                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
                              </Tooltip>
                              
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTextExpand(task.id, 'notes');
                                }}
                                sx={{
                                  position: 'absolute',
                                  bottom: -8,
                                  right: -8,
                                  backgroundColor: theme.palette.primary.main,
                                  color: theme.palette.primary.contrastText,
                                  width: 24,
                                  height: 24,
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                  },
                                  display: { xs: 'flex', md: 'none' },
                                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }}
                              >
                                <ExpandMore sx={{ fontSize: 16 }} />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>

                  {/* Progress Section - Always at bottom */}
                  <Box sx={{ mt: 'auto' }}>
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={calculateProgress(task.status)}
                        sx={{ 
                          height: 6,
                          borderRadius: 3,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getProgressBarColor(task.status),
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>

                    <Typography variant="body2" sx={{ 
                      fontWeight: 500,
                      color: task.status === 'Done' ? theme.palette.success.main : 
                             ['Cancelled', 'Blocked'].includes(task.status) ? theme.palette.error.main :
                             task.status === 'On Hold' ? theme.palette.warning.main : 
                             theme.palette.text.secondary
                    }}>
                      {calculateProgress(task.status)}% Complete
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default MultiViewTaskManagement;
